import React from 'react'
import { defineMessages, IntlShape } from 'react-intl'

import { Appointment } from '../../models/appointment/Appointment'
import { ConversationObject } from '../../models/conversation/Conversation'
import { type Episode, EpisodeStates } from '../../models/episodes/Episodes'
import { CONVERSATION_STATE, CONVERSATION_TYPE, MessageConversation } from '../../models/messages/Messages'
import { ProgramNames } from '../../models/programs/Programs'
import { ProviderEnums, ProviderInfo, ProviderProgramTaxonomy } from '../../models/providers/Providers'
import { fallbackICASPhoneNumber } from '../../models/searchForCare/SearchForCare'
import {
  CARE_ADVISOR_DISPLAY_PHONE_NUMBER,
  CARE_NAVIGATOR_EXTENSION,
  LOWER_RISK_GUARDRAIL_EXTENSION,
} from '../common/constants/constants'
import { isCTPOForProgram } from '../programConfigs/programHelpers'
import {
  getProviderTitle,
  isCaregiverTrainingForNeuroProvider,
  isManagerCoachingProvider,
  isNeurodiversityProvider,
} from '../providers/utils'

const childBadgeMessages = defineMessages({
  childBadge: {
    defaultMessage: 'For {name}',
    description: 'Badge label shown above a messaging thread that belongs to a child/dependent of the logged-in parent',
  },
})

const messageThreadProviderSubtitleMessages = defineMessages({
  mentalHealthCoach: {
    defaultMessage: 'Mental health coach',
    description: 'Provider role shown below a mental health coach name in the messaging conversation list',
  },
  managerGrowthCoach: {
    defaultMessage: 'Manager growth coach',
    description: 'Provider role shown below a manager growth coach name in the messaging conversation list',
  },
})

/**
 * Returns the provider role shown in a messaging thread.
 *
 * Messaging uses sentence-case coach labels, while provider profiles intentionally
 * use title case. The conversation's episode taxonomy disambiguates multi-program
 * coaches; provider-level taxonomy is only used for single-program coaches.
 */
export const getMessageThreadProviderSubtitle = (
  intl: IntlShape,
  provider: ProviderInfo,
  episodeProgramTaxonomy?: ProviderProgramTaxonomy,
  isCaregiverTrainingProviderTitlesEnabled = false,
): string => {
  if (provider.lyra_type?.toUpperCase() === ProviderEnums.COACH) {
    if (!episodeProgramTaxonomy && (provider.program_taxonomy?.length ?? 0) > 1) {
      return intl.formatMessage(messageThreadProviderSubtitleMessages.mentalHealthCoach)
    }

    const programTaxonomy = episodeProgramTaxonomy ? [episodeProgramTaxonomy] : (provider.program_taxonomy ?? [])

    if (isManagerCoachingProvider({ programTaxonomy })) {
      return intl.formatMessage(messageThreadProviderSubtitleMessages.managerGrowthCoach)
    }

    if (isNeurodiversityProvider({ programTaxonomy }) || isCaregiverTrainingForNeuroProvider({ programTaxonomy })) {
      return getProviderTitle(
        intl,
        provider,
        undefined,
        episodeProgramTaxonomy,
        isCaregiverTrainingProviderTitlesEnabled,
      )
    }

    return intl.formatMessage(messageThreadProviderSubtitleMessages.mentalHealthCoach)
  }

  return getProviderTitle(intl, provider, undefined, episodeProgramTaxonomy, isCaregiverTrainingProviderTitlesEnabled)
}

export const getChildBadgeLabel = (
  intl: IntlShape,
  appointments: Appointment[],
  patientLyraId: string,
  currentUserId: string,
): string | undefined => {
  if (!currentUserId || patientLyraId === currentUserId) {
    return undefined
  }
  const match = appointments.find((a) => {
    if (a.childLyraId === patientLyraId) {
      return true
    }
    if (a.guardianLyraId && a.userId === patientLyraId) {
      return true
    }
    return false
  })
  if (!match) {
    return undefined
  }
  const firstName = match.childFirstName ?? match.userInfo?.firstName
  if (!firstName) {
    return undefined
  }
  return intl.formatMessage(childBadgeMessages.childBadge, { name: firstName })
}

type SubtitleParameters = {
  isProviderBlocked: boolean
  countryEmergencyNumbers: string[]
  sessionTime: string
  inVideoSession: boolean
  hasActiveAppointment: boolean
  isGenAI: boolean
  conversationType?: CONVERSATION_TYPE
  isProviderDA?: boolean
  providerName?: string
  contactCarePhoneNumber?: string
  renderLink: (phone: string) => React.ReactNode
  renderContactLyraLink: () => React.ReactNode
}

export const getChatHeaderSubTitleTooltipMsg = (
  isGenAI: boolean,
  conversationType?: CONVERSATION_TYPE,
): string | undefined => {
  if (!isGenAI || conversationType !== CONVERSATION_TYPE.MANAGER_COACHING_AI) {
    return undefined
  }

  return 'Lyra AI is not human. Do not use it for crisis or HR support. Please contact your HR team for all matters concerning employees, policies, compliance, and workplace conduct. You can contact Lyra’s Organizational Services team for manager consultations at (877) 505-7147, ext 4.'
}

export const getChatHeaderSubTitleMsg = ({
  isProviderBlocked,
  countryEmergencyNumbers,
  inVideoSession,
  hasActiveAppointment,
  sessionTime,
  isGenAI,
  conversationType,
  providerName,
  isProviderDA,
  contactCarePhoneNumber,
  renderLink,
  renderContactLyraLink,
}: SubtitleParameters) => {
  if (isProviderBlocked) {
    if (isProviderDA && contactCarePhoneNumber && countryEmergencyNumbers) {
      return React.createElement(
        React.Fragment,
        null,
        `is unavailable. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    return `is unavailable. Call ${countryEmergencyNumbers} for emergencies.`
  }
  if (inVideoSession) {
    return ''
  }
  if (isGenAI) {
    if (conversationType === CONVERSATION_TYPE.MANAGER_COACHING_AI) {
      return React.createElement(
        React.Fragment,
        null,
        `Lyra AI can make mistakes. For urgent support, contact your HR Team or Lyra. For emergencies, call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    return React.createElement(
      React.Fragment,
      null,
      `Lyra AI isn’t human and can make mistakes. Do not use it for crisis support. For urgent help, `,
      renderContactLyraLink(),
      ` or `,
      renderLink(countryEmergencyNumbers[0]),
      `.`,
    )
  }
  if (hasActiveAppointment) {
    return `Session today from ${sessionTime}`
  }
  if (isProviderDA) {
    if (providerName && contactCarePhoneNumber && countryEmergencyNumbers) {
      // Build a React fragment with clickable tel: links using the provided renderer
      return React.createElement(
        React.Fragment,
        null,
        `${providerName} will respond as soon as they can. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    // Fallback to plain string if a renderer isn't provided
    return `${
      providerName ?? 'Your provider'
    } will respond as soon as they can. For immediate crisis support, contact ${contactCarePhoneNumber}, ext 2, or call ${countryEmergencyNumbers}.`
  }
  return `Usually responds within 2 days. Call ${countryEmergencyNumbers} for emergencies.`
}

export type AiConversationLabels = {
  threadTitle: string
  threadSubtitle: string
  chatTitle: string
}

type EpisodeLinkedConversation = MessageConversation | ConversationObject

/**
 * AI conversations use the generic Lyra AI label when only one coaching type is
 * available. When both types are available, the thread title identifies the
 * coaching context and the chat title combines that context with Lyra AI.
 */
export const getAiConversationLabels = (
  conversationType: CONVERSATION_TYPE | undefined,
  hasMultipleAiTypes: boolean,
): AiConversationLabels => {
  const coachingTypeLabel =
    conversationType === CONVERSATION_TYPE.MANAGER_COACHING_AI
      ? 'Manager Growth Coaching'
      : conversationType === CONVERSATION_TYPE.AI_COACH
        ? 'Mental Health Coaching'
        : undefined

  if (!hasMultipleAiTypes || !coachingTypeLabel) {
    return {
      threadTitle: 'Lyra AI',
      threadSubtitle: '',
      chatTitle: 'Lyra AI',
    }
  }

  return {
    threadTitle: coachingTypeLabel,
    threadSubtitle: 'Lyra AI',
    chatTitle: `Lyra AI \u2022 ${coachingTypeLabel}`,
  }
}

export const isAiConversationType = (type?: CONVERSATION_TYPE): type is CONVERSATION_TYPE =>
  type === CONVERSATION_TYPE.AI_COACH || type === CONVERSATION_TYPE.MANAGER_COACHING_AI

export const isVisibleAiConversation = (conversation: EpisodeLinkedConversation) => {
  return (
    isAiConversationType(conversation.conversation_attributes?.type) &&
    (conversation.conversation_state === CONVERSATION_STATE.ACTIVE ||
      conversation.conversation_state === CONVERSATION_STATE.INACTIVE)
  )
}

export const isActiveAiConversation = (conversation: EpisodeLinkedConversation) => {
  return (
    isAiConversationType(conversation.conversation_attributes?.type) &&
    conversation.conversation_state === CONVERSATION_STATE.ACTIVE
  )
}

/**
 * showAiCoach: true if the AI Coach feature is enabled and there is at least one active AI Coach conversation.
 * showAiCoachConversation: true if the AI Coach feature is enabled and there is at least one visible AI Coach conversation (active or inactive).
 */
export type AICoachVisibilityState = {
  showAiCoach: boolean
  showAiCoachConversation: boolean
}

export const isActiveGoogleCatalystEpisode = (episode: Episode): boolean =>
  episode.state === EpisodeStates.IN_PROGRESS &&
  isCTPOForProgram(episode.program_taxonomy, ProgramNames.PerformanceCoaching)

export const hasMultipleAiConversationTypes = (conversations: readonly EpisodeLinkedConversation[]): boolean => {
  const types = new Set<CONVERSATION_TYPE>()
  for (const c of conversations) {
    const type = c.conversation_attributes?.type
    if (type && isAiConversationType(type)) {
      types.add(type)
      if (types.size > 1) return true
    }
  }
  return false
}

const getConversationEpisodeIds = (conversation: EpisodeLinkedConversation): string[] => {
  const episodeIds = conversation.episode_ids?.length
    ? conversation.episode_ids
    : [conversation.conversation_attributes?.episode_id]

  return episodeIds.filter((episodeId): episodeId is string => episodeId !== undefined)
}

const hasAiCoachConversationForActiveGoogleCatalystEpisode = ({
  conversations,
  episodes,
}: {
  conversations: readonly EpisodeLinkedConversation[]
  episodes: readonly Episode[]
}): boolean => {
  const activeGoogleCatalystEpisodeIds = new Set(
    episodes.filter(isActiveGoogleCatalystEpisode).map((episode) => episode.id),
  )

  return conversations.some((conversation) => {
    return (
      conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH &&
      getConversationEpisodeIds(conversation).some((episodeId) => activeGoogleCatalystEpisodeIds.has(episodeId))
    )
  })
}

export const getGenAiOptOutWithGoogleCatalystOverride = ({
  conversations,
  episodes,
  genAiOptOut,
  isGoogleCatalystLyraAiEnabled,
}: {
  conversations: readonly EpisodeLinkedConversation[]
  episodes: readonly Episode[]
  genAiOptOut: boolean
  isGoogleCatalystLyraAiEnabled: boolean
}): boolean => {
  if (
    genAiOptOut &&
    isGoogleCatalystLyraAiEnabled &&
    hasAiCoachConversationForActiveGoogleCatalystEpisode({
      conversations,
      episodes,
    })
  ) {
    return false
  }

  return genAiOptOut
}

export const getAiCoachPromoState = ({
  conversations,
  isAiCoachEnabled,
}: {
  conversations: MessageConversation[]
  isAiCoachEnabled: boolean
}): AICoachVisibilityState => {
  const showAiCoachConversation = isAiCoachEnabled && conversations.some(isVisibleAiConversation)
  const showAiCoach = isAiCoachEnabled && conversations.some(isActiveAiConversation)

  return {
    showAiCoach,
    showAiCoachConversation,
  }
}

export const getAiCoachConversationForEpisode = (
  conversations: MessageConversation[],
  episodeId: string | undefined,
): MessageConversation | undefined => {
  if (!episodeId) return undefined
  return conversations.find((c) => isActiveAiConversation(c) && getConversationEpisodeIds(c).includes(episodeId))
}

export const getClientHasActiveAiConversation = (
  clientId: string,
  conversations?: readonly EpisodeLinkedConversation[],
) => {
  const hasActiveAiConversation = conversations?.some(
    (conversation) => conversation.patient_lyra_id === clientId && isActiveAiConversation(conversation),
  )
  return Boolean(hasActiveAiConversation)
}

// Default US GenAI route for higher-risk or unclassified guardrails.
export const US_GEN_AI_TOLL_FREE_NUMBER = `${CARE_ADVISOR_DISPLAY_PHONE_NUMBER}, ${CARE_NAVIGATOR_EXTENSION}`

const LOWER_RISK_GUARDRAIL_TYPES = new Set(['WORKPLACE_VIOLENCE', 'DISCRIMINATION_HARASSMENT'])

// Default US emergency number used as a fallback for {{emergency_phone_number}}.
export const DEFAULT_EMERGENCY_PHONE_NUMBER = '911'

/**
 * Resolves the toll-free phone number to inject for {{toll_free_number}} in
 * Lyra AI responses. US is a special case — the number isn't stored in
 * country.tollFreeNumbersList and its extension depends on the guardrail that
 * produced the message. Otherwise use the first entry from the list, falling
 * back to fallbackICASPhoneNumber when none is available.
 */
export const resolveGenAiTollFreeNumber = (
  countryIsoCode: string | undefined | null,
  tollFreeNumbersList: string[] | undefined | null,
  guardrailType?: string | null,
): string => {
  if (countryIsoCode === 'US') {
    return guardrailType && LOWER_RISK_GUARDRAIL_TYPES.has(guardrailType)
      ? `${CARE_ADVISOR_DISPLAY_PHONE_NUMBER}, ${LOWER_RISK_GUARDRAIL_EXTENSION}`
      : US_GEN_AI_TOLL_FREE_NUMBER
  }
  return tollFreeNumbersList?.[0] || fallbackICASPhoneNumber
}

/**
 * Resolves the emergency phone number to inject for {{emergency_phone_number}}
 * in Lyra AI responses. Uses the first entry from the country's emergency
 * numbers list, falling back to '911' when the list is missing or empty.
 */
export const resolveGenAiEmergencyNumber = (emergencyPhoneNumbersList: string[] | undefined | null): string => {
  return emergencyPhoneNumbersList?.[0] || DEFAULT_EMERGENCY_PHONE_NUMBER
}
